import { Injectable } from '@angular/core';
import { BaseLocalStorageService } from '@shared/data-access';
import { CacheEvidencesInitiator, CacheService, CacheUsersInitiator } from '@shared/utils/cache';
import { BaseAuthService, BaseUser, UserRole } from '@shared/utils/base-auth';
import { combineLatest, EMPTY, Observable, switchMap, take } from 'rxjs';
import { ListOfficerFacade, Officer } from '@shared/feature/list-officer';
import { LoggedInOfficer, OfficerDetailFacade } from '@intrensic/shared/officer-details';
import { ListEvidenceFacade } from '@shared/feature/list-evidence';
import { TableData } from '@shared/ui/tables';
import { CacheUsersHelperService } from './cache-users-helper.service';

const DEFAULT_USERS_QUERY = '$skip=0&$count=true&$orderby=EmployeeID desc&$format=json&$top=10';
const DEFAULT_EVIDENCE_QUERY = '$skip=0&$count=true&$orderby=OriginalFileDate desc&$format=json&$top=10';

@Injectable({
  providedIn: 'root',
})
export class CacheImplementationService extends CacheService {
  private userQueryKey = 'user-query-key';
  private evidenceQueryKey = 'file-query-key';
  private readonly quotationMarksRegex = /^"(.*)"$/;

  constructor(
    private officerFacade: OfficerDetailFacade,
    private localStorage: BaseLocalStorageService,
    private baseAuthService: BaseAuthService,
    private listOfficerFacade: ListOfficerFacade,
    private listEvidenceFacade: ListEvidenceFacade,
    private cacheUsersHelperService: CacheUsersHelperService
  ) {
    super();
  }

  override cacheUsers(userUid: string, initiator: CacheUsersInitiator, identifier?: string): Observable<void> {
    const userLocalStorageQuery = this.getUserLocalStorageQuery(userUid);

    return combineLatest<[LoggedInOfficer, string, TableData<Officer>]>([
      this.officerFacade.getLoggedInOfficer().pipe(take(1)),
      this.listOfficerFacade.getOfficersQuery().pipe(take(1)),
      this.listOfficerFacade.getPaginationOfficers().pipe(take(1)),
    ]).pipe(
      switchMap(([officer, userNgrxStoreQuery, paginationOfficers]) => {
        if (officer.role === UserRole.SuperAdmin || officer.role === UserRole.HRAdmin) {
          switch (initiator) {
            case CacheUsersInitiator.InitialCache:
            case CacheUsersInitiator.UserUpdated:
              this.listOfficerFacade.listPaginationOfficers(userLocalStorageQuery);
              break;
            case CacheUsersInitiator.UserCreated:
              this.listOfficerFacade.listPaginationOfficers(userNgrxStoreQuery);
              break;
            case CacheUsersInitiator.UserDeleted:
              this.cacheUsersHelperService.handleListPaginationOfficersOnOfficerDeletion(userNgrxStoreQuery, paginationOfficers.count, identifier ?? '', officer.autoClearFilters);
              break;
            case CacheUsersInitiator.PageLeave:
            case CacheUsersInitiator.AutoClearFiltersUpdated:
            default:
              if (officer.autoClearFilters && userLocalStorageQuery !== userNgrxStoreQuery) {
                this.listOfficerFacade.listPaginationOfficers(userLocalStorageQuery);
              }
              break;
          }
        }

        return EMPTY;
      })
    );
  }

  override cacheEvidences(userUid: string, initiator: CacheEvidencesInitiator): Observable<void> {
    const evidenceLocalStorageQuery = this.getEvidenceLocalStorageQuery(userUid);

    return combineLatest<[LoggedInOfficer, string]>([this.officerFacade.getLoggedInOfficer().pipe(take(1)), this.listEvidenceFacade.getEvidenceQuery().pipe(take(1))]).pipe(
      switchMap(([officer, evidenceNgrxStoreQuery]) => {
        if (officer.role !== UserRole.HRAdmin) {
          switch (initiator) {
            case CacheEvidencesInitiator.InitialCache:
            case CacheEvidencesInitiator.EvidenceUploaded:
            case CacheEvidencesInitiator.EvidenceUpdated:
            case CacheEvidencesInitiator.EvidenceProcessed:
              this.listEvidenceFacade.listEvidence(evidenceLocalStorageQuery);
              break;
            case CacheEvidencesInitiator.PageLeave:
            case CacheEvidencesInitiator.AutoClearFiltersUpdated:
            default:
              if (officer.autoClearFilters && evidenceLocalStorageQuery !== evidenceNgrxStoreQuery) {
                this.listEvidenceFacade.listEvidence(evidenceLocalStorageQuery);
              }
              break;
          }
        }

        return EMPTY;
      })
    );
  }

  override storeUserDefaultQueryLocalStorage(userUid: string): void {
    this.localStorage.setItem(`${this.userQueryKey}-${userUid}`, DEFAULT_USERS_QUERY);
  }

  override storeEvidenceDefaultQueryLocalStorage(userUid: string): void {
    this.localStorage.setItem(`${this.evidenceQueryKey}-${userUid}`, DEFAULT_EVIDENCE_QUERY);
  }

  override storeUserQueryLocalStorage(query: string): void {
    combineLatest<[LoggedInOfficer, BaseUser | undefined]>([
      this.officerFacade.getLoggedInOfficer().pipe(take(1)),
      this.baseAuthService.getCurrentBaseUser().pipe(take(1)),
    ]).subscribe(([officer, user]) => {
      this.localStorage.setItem(`${this.userQueryKey}-${user?.uid}`, !officer.autoClearFilters && query ? query : DEFAULT_USERS_QUERY);
    });
  }

  override storeEvidenceQueryLocalStorage(query: string): void {
    combineLatest<[LoggedInOfficer, BaseUser | undefined]>([
      this.officerFacade.getLoggedInOfficer().pipe(take(1)),
      this.baseAuthService.getCurrentBaseUser().pipe(take(1)),
    ]).subscribe(([officer, user]) => {
      this.localStorage.setItem(`${this.evidenceQueryKey}-${user?.uid}`, !officer.autoClearFilters && query ? query : DEFAULT_EVIDENCE_QUERY);
    });
  }

  override usersDataModified(initiator: CacheUsersInitiator): void {
    this.usersDataModified$.next(initiator);
  }

  override evidencesDataModified(initiator: CacheEvidencesInitiator): void {
    this.evidencesDataModified$.next(initiator);
  }

  override autoClearFiltersUpdated(autoClearFilters: boolean): void {
    this.autoClearFiltersUpdated$.next(autoClearFilters);
  }

  override getUserLocalStorageQuery(userUid: string): string {
    let userLocalStorageQuery = this.localStorage.getItem(`${this.userQueryKey}-${userUid}`) as string;

    return (userLocalStorageQuery = userLocalStorageQuery ? userLocalStorageQuery.replace(this.quotationMarksRegex, '$1') : DEFAULT_USERS_QUERY);
  }

  override getEvidenceLocalStorageQuery(userUid: string): string {
    let evidenceLocalStorageQuery = this.localStorage.getItem(`${this.evidenceQueryKey}-${userUid}`) as string;

    return (evidenceLocalStorageQuery = evidenceLocalStorageQuery ? evidenceLocalStorageQuery.replace(this.quotationMarksRegex, '$1') : DEFAULT_EVIDENCE_QUERY);
  }

  override clearFiltersFromLocalStorage(identifier: string): void {
    this.localStorage.removeItem(`${identifier} filters`);
    this.localStorage.removeItem(`${identifier} sort`);
    this.localStorage.removeItem(`${identifier} page`);
  }
}
